// SPDX-License-Identifier: AGPL-3.0-or-later

import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import * as CustomSoundDB from '@app/features/notification/utils/CustomSoundDB';
import type {EntranceSoundScope} from '@app/features/notification/utils/EntranceSoundScopes';
import {
	getEntranceSoundStorageKey,
	resolveEntranceSoundFromMap,
} from '@app/features/notification/utils/EntranceSoundScopes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useEffect, useMemo, useState} from 'react';

const logger = new Logger('useEntranceSound');
export const useEntranceSound = (selectedScope: EntranceSoundScope) => {
	const {t} = useLingui();
	const [entranceSoundsByScope, setEntranceSoundsByScope] = useState<Record<string, CustomSoundDB.EntranceSound>>({});
	const [isPreviewing, setIsPreviewing] = useState(false);
	const hasEntranceSounds = useMemo(
		() =>
			isLimitToggleEnabled(
				{feature_voice_entrance_sounds: LimitResolver.resolve({key: 'feature_voice_entrance_sounds', fallback: 0})},
				'feature_voice_entrance_sounds',
			),
		[],
	);
	const refreshEntranceSounds = useCallback(async () => {
		try {
			const sounds = await CustomSoundDB.getAllEntranceSounds();
			setEntranceSoundsByScope(sounds);
		} catch (error) {
			logger.error('Failed to load entrance sounds', error);
		}
	}, []);
	useEffect(() => {
		void refreshEntranceSounds();
	}, [refreshEntranceSounds]);
	const selectedScopeStorageKey = useMemo(() => getEntranceSoundStorageKey(selectedScope), [selectedScope]);
	const entranceSound = entranceSoundsByScope[selectedScopeStorageKey] ?? null;
	const resolvedEntranceSound = useMemo(
		() => resolveEntranceSoundFromMap(selectedScope, entranceSoundsByScope),
		[entranceSoundsByScope, selectedScope],
	);
	const activeEntranceSound = resolvedEntranceSound?.sound ?? null;
	const inheritedEntranceSound = entranceSound ? null : activeEntranceSound;
	const inheritedFromScope = entranceSound ? null : (resolvedEntranceSound?.scope ?? null);
	const upload = useCallback(
		async (file: File | null) => {
			if (!hasEntranceSounds || !file) {
				return;
			}
			const validation = CustomSoundDB.isValidAudioFile(file);
			if (!validation.valid) {
				ToastCommands.createToast({
					type: 'error',
					children: validation.error || t`Invalid audio file`,
				});
				return;
			}
			const durationValidation = await CustomSoundDB.validateAudioDuration(file);
			if (!durationValidation.valid) {
				ToastCommands.createToast({
					type: 'error',
					children: durationValidation.error || t`Audio is too long`,
				});
				return;
			}
			try {
				await CustomSoundDB.saveEntranceSound(selectedScope, file, file.name, durationValidation.duration!);
				await refreshEntranceSounds();
				ToastCommands.createToast({
					type: 'success',
					children: t`Entrance sound saved`,
				});
			} catch (error) {
				logger.error('Failed to upload entrance sound', error);
				ToastCommands.createToast({
					type: 'error',
					children: t`Couldn't save entrance sound`,
				});
			}
		},
		[hasEntranceSounds, refreshEntranceSounds, selectedScope, t],
	);
	const remove = useCallback(async () => {
		try {
			await CustomSoundDB.deleteEntranceSound(selectedScope);
			await refreshEntranceSounds();
			ToastCommands.createToast({
				type: 'success',
				children: t`Entrance sound removed`,
			});
		} catch (error) {
			logger.error('Failed to delete entrance sound', error);
			ToastCommands.createToast({
				type: 'error',
				children: t`Couldn't remove entrance sound`,
			});
		}
	}, [refreshEntranceSounds, selectedScope, t]);
	const preview = useCallback(async () => {
		if (!activeEntranceSound || isPreviewing) return;
		try {
			setIsPreviewing(true);
			const url = URL.createObjectURL(activeEntranceSound.blob);
			const audio = new Audio(url);
			audio.onended = () => {
				URL.revokeObjectURL(url);
				setIsPreviewing(false);
			};
			audio.onerror = () => {
				URL.revokeObjectURL(url);
				setIsPreviewing(false);
			};
			await audio.play();
		} catch (error) {
			logger.error('Failed to preview entrance sound', error);
			setIsPreviewing(false);
		}
	}, [activeEntranceSound, isPreviewing]);
	const openUploadDialog = useCallback(async () => {
		if (!hasEntranceSounds) {
			PremiumModalCommands.open();
			return;
		}
		const [file] = await openFilePicker({accept: CustomSoundDB.SUPPORTED_MIME_TYPES.join(',')});
		await upload(file ?? null);
	}, [hasEntranceSounds, upload]);
	return {
		entranceSound,
		activeEntranceSound,
		inheritedEntranceSound,
		inheritedFromScope,
		isPreviewing,
		upload,
		remove,
		preview,
		openUploadDialog,
	};
};
